import { useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';

import { useSAL2060_R01Store } from './Store_SAL2060_R01';
import type { SAL2060_R01Filter } from './Store_SAL2060_R01';
import {
    getSAL2060_R01HeaderList,
    getSAL2060_R01HeaderAllCount,
    getSAL2060_R01DetailList,
    getSAL2060_R01DetailAllCount,
    downloadExcel_SAL2060_R01,
} from './Api_SAL2060_R01';
import { downloadExcel } from "src/utils/blob/blobUtil";
import { SAL2060_R01HeaderDto, SAL2060_R01DetailDto } from './Dto_SAL2060_R01';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import {gridRefSelectedRowHandler} from "src/utils/grid/GridHandlerContent";

interface Props {
    headerGridRef: React.RefObject<AgGridReact<SAL2060_R01HeaderDto> | null>;
    detailGridRef: React.RefObject<AgGridReact<SAL2060_R01DetailDto> | null>;
    setMessage: (message: string) => void;
}

export const Content_SAL2060_R01 = (props: Props) => {
    const { headerGridRef, detailGridRef, setMessage } = props;


    useFetchActionbarAuth("SAL2060_R01");

    // ========================================
    // Store
    // ========================================
    const {
        filter,
        pastFilter,
        setPastFilter,
        setHeaderList,
        setHeaderAllCount,
        setDetailList,
        setDetailAllCount,
        setSelectedCustomCd,
        initData,
    } = useSAL2060_R01Store();

    const { setLoadingSpinner } = useLoadingSpinnerStore();

    // 권한 체크 (메뉴 등록 전까지 임시 주석)
    // useFetchActionbarAuth("SAL2060_R01");

    // ========================================
    // Header 전체 건수 조회
    // ========================================
    const renewalHeaderAllCount = useCallback(async () => {
        try {
            const response = await getSAL2060_R01HeaderAllCount(filter);
            if (response.data.successFlag) {
                setHeaderAllCount(response.data.data || 0);
                return response.data.data || 0;
            }
            return 0;
        } catch (error) {
            showError("거래처별 집계 건수 조회 중 오류가 발생했습니다.");
            return 0;
        }
    }, [filter, setHeaderAllCount]);

    // ========================================
    // Detail 전체 건수 조회
    // ========================================
    const renewalDetailAllCount = useCallback(async (customCd: string) => {
        try {
            const response = await getSAL2060_R01DetailAllCount(customCd, filter);
            if (response.data.successFlag) {
                setDetailAllCount(response.data.data || 0);
                return response.data.data || 0;
            }
            return 0;
        } catch (error) {
            showError("상세 내역 건수 조회 중 오류가 발생했습니다.");
            return 0;
        }
    }, [filter, setDetailAllCount]);



    // ========================================
    // Detail (상세 내역) 조회
    // ========================================
    const fetchDetailData = useCallback(
        async (customCd: string) => {
            setLoadingSpinner(true);

            try {
                setSelectedCustomCd(customCd);

                // Detail 조회
                const response = await getSAL2060_R01DetailList(
                    customCd,
                    filter,
                    'search'
                );

                if (response.data.successFlag) {
                    const detailData = response.data.data || [];
                    setDetailList(detailData);

                    // 전체 건수 조회
                    const allCount = await renewalDetailAllCount(customCd);

                    if (detailData.length > 0) {
                        ShowSuccessFetch(
                            setMessage,
                            `상세 내역 조회 완료되었습니다 (총 ${detailData.length}/${allCount}건)`
                        );
                    } else {
                        showWarning('상세 내역 데이터가 없습니다.');
                        setDetailList([]);
                        setDetailAllCount(0);
                    }
                } else {
                    showError(response.data.message || '조회 실패');
                }
            } catch (error) {
                console.error('Detail 조회 오류:', error);
                showError('조회 중 오류가 발생했습니다.');
            } finally {
                setLoadingSpinner(false);
            }
        },
        [
            filter,
            setSelectedCustomCd,
            setDetailList,
            setDetailAllCount,
            setLoadingSpinner,
            setMessage,
            renewalDetailAllCount,
        ]
    );

    // ========================================
    // Header (거래처별 집계) 조회
    // ========================================
    // ========================================
// Header (거래처별 집계) 조회
// ========================================
    const fetchHeaderData = useCallback(async () => {
        setLoadingSpinner(true);

        try {
            // Header 조회
            const response = await getSAL2060_R01HeaderList(filter, 'search');

            if (response.data.successFlag) {
                const headerData = response.data.data || [];

                setHeaderList(headerData);
                setPastFilter(filter);

                // 전체 건수 조회
                const allCount = await renewalHeaderAllCount();

                if (headerData.length > 0) {
                    // ✅ 첫 번째 행 자동 선택 + Detail 조회
                    setTimeout(() => {
                        const firstCustomCd = headerData[0].customCd;
                        if (firstCustomCd) {
                            // 첫 행 선택 표시
                            const api = headerGridRef.current?.api;
                            if (api) {
                                api.ensureIndexVisible(0, 'top');
                                const firstNode = api.getDisplayedRowAtIndex(0);
                                if (firstNode) {
                                    firstNode.setSelected(true);
                                }
                            }

                            // Detail 자동 조회
                            fetchDetailData(firstCustomCd);
                        }
                    }, 100);

                    ShowSuccessFetch(
                        setMessage,
                        `거래처별 집계 조회 완료되었습니다 (총 ${headerData.length}/${allCount}건)`
                    );
                } else {
                    showWarning('데이터가 없습니다.');
                    setDetailList([]);
                    setDetailAllCount(0);
                    setSelectedCustomCd(null);
                }
            } else {
                showError(response.data.message || '조회 실패');
            }
        } catch (error) {
            console.error('Header 조회 오류:', error);
            showError('조회 중 오류가 발생했습니다.');
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        filter,
        setPastFilter,
        setHeaderList,
        setHeaderAllCount,
        setDetailList,
        setDetailAllCount,
        setSelectedCustomCd,
        setLoadingSpinner,
        setMessage,
        renewalHeaderAllCount,
        fetchDetailData,
        headerGridRef,
    ]);


    // ========================================
    // 엑셀 다운로드
    // ========================================
    const handleExcelClick = useCallback(async () => {
        setLoadingSpinner(true);
        try {
            const response = await downloadExcel_SAL2060_R01(pastFilter);

            const data = response.data.data;
            let blob: Blob;

            if (Array.isArray(data)) {
                blob = new Blob([new Uint8Array(data)], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
            } else if (typeof data === 'string') {
                const binary = atob(data);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) {
                    bytes[i] = binary.charCodeAt(i);
                }
                blob = new Blob([bytes], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
            } else {
                throw new Error('지원하지 않는 데이터 형식입니다.');
            }

            downloadExcel(blob, '거래처별매출현황.xlsx');
            ShowSuccessFetch(setMessage, '엑셀 다운로드 완료');
        } catch (error) {
            showError('엑셀 다운로드 중 오류가 발생했습니다.');
            console.error('SAL2060_R01 handleExcelClick error:', error);
        } finally {
            setLoadingSpinner(false);
        }
    }, [pastFilter, setLoadingSpinner, setMessage]);

    // ========================================
    // 초기화
    // ========================================
    const initContainerData = useCallback(() => {
        initData();
        setMessage('초기화되었습니다.');
    }, [initData, setMessage]);

    // ========================================
    // Return
    // ========================================
    return {
        fetchHeaderData,
        fetchDetailData,
        handleExcelClick,
        initContainerData,
    };
};

export default Content_SAL2060_R01;
